import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Loader2, Trash2, Check } from "lucide-react";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

function RemovePdfPages() {
  const [file, setFile] = useState(null);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [pageImages, setPageImages] = useState([]);
  const [selectedPages, setSelectedPages] = useState(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Remove PDF Pages - Delete Pages from PDF - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setSelectedPages(new Set());
      await loadPdf(selectedFile);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setSelectedPages(new Set());
      await loadPdf(droppedFile);
    } else {
      toast.error("Please drop a valid PDF file");
    }
  };

  const loadPdf = async (pdfFile) => {
    setIsProcessing(true);
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      setPdfBytes(new Uint8Array(arrayBuffer));
      
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const images = [];
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 0.5 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        await page.render({ canvasContext: context, viewport }).promise;
        images.push({
          pageNum: i,
          dataUrl: canvas.toDataURL()
        });
      }
      
      setPageImages(images);
      toast.success(`PDF loaded: ${pdf.numPages} pages. Select pages to remove.`);
    } catch (error) {
      console.error("Error loading PDF:", error);
      toast.error("Failed to load PDF");
    }
    setIsProcessing(false);
  };

  const togglePage = (pageNum) => {
    const newSelected = new Set(selectedPages);
    if (newSelected.has(pageNum)) {
      newSelected.delete(pageNum);
    } else {
      newSelected.add(pageNum);
    }
    setSelectedPages(newSelected);
  };

  const selectAll = () => {
    if (selectedPages.size === pageImages.length) {
      setSelectedPages(new Set());
    } else {
      setSelectedPages(new Set(pageImages.map(p => p.pageNum)));
    }
  };

  const removePagesAndSave = async () => {
    if (selectedPages.size === 0) {
      toast.error("Please select pages to remove");
      return;
    }

    if (selectedPages.size === pageImages.length) {
      toast.error("Cannot remove all pages from PDF");
      return;
    }

    setIsProcessing(true);
    try {
      const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
      
      // Get pages to keep (not selected for removal)
      const pagesToKeep = pageImages
        .filter(p => !selectedPages.has(p.pageNum))
        .map(p => p.pageNum - 1);
      
      // Create new document with only kept pages
      const newPdfDoc = await PDFDocument.create();
      const copiedPages = await newPdfDoc.copyPages(pdfDoc, pagesToKeep);
      copiedPages.forEach(page => newPdfDoc.addPage(page));
      
      const newPdfBytes = await newPdfDoc.save();
      const blob = new Blob([newPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name.replace('.pdf', '_pages_removed.pdf');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Removed ${selectedPages.size} pages. Download started!`);
    } catch (error) {
      console.error("Error removing pages:", error);
      toast.error("Failed to remove pages: " + error.message);
    }
    setIsProcessing(false);
  };

  const clearFile = () => {
    setFile(null);
    setPdfBytes(null);
    setPageImages([]);
    setSelectedPages(new Set());
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-rose-50 to-pink-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
              <Trash2 className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-full mb-4">
            <Trash2 className="w-5 h-5" />
            <span className="font-medium">Remove PDF Pages</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Remove Pages from PDF
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Select and delete unwanted pages from your PDF document.
          </p>
        </div>

        {/* Upload Area */}
        {!file && (
          <Card 
            className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 max-w-2xl mx-auto ${
              isDragging ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20' : 'border-slate-300 dark:border-slate-600'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CardContent className="p-12 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone"
              >
                <Upload className="w-16 h-16 mx-auto mb-4 text-rose-500" />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  Drop PDF or click to upload
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  Select a PDF to remove pages from
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {file && (
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Controls */}
            <Card className="dark:bg-slate-800">
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <FileText className="w-8 h-8 text-rose-500" />
                  <div>
                    <h3 className="font-semibold dark:text-white">{file.name}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {pageImages.length} pages • {selectedPages.size} selected for removal
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll}>
                    {selectedPages.size === pageImages.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={clearFile}>
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={removePagesAndSave}
                    disabled={selectedPages.size === 0 || isProcessing}
                    className="bg-gradient-to-r from-rose-500 to-pink-600"
                  >
                    {isProcessing ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-1" />
                    )}
                    Remove {selectedPages.size} Page{selectedPages.size !== 1 ? 's' : ''}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Page Grid */}
            {isProcessing && pageImages.length === 0 ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {pageImages.map((page) => {
                  const isSelected = selectedPages.has(page.pageNum);
                  return (
                    <Card 
                      key={page.pageNum} 
                      className={`cursor-pointer overflow-hidden transition-all hover:shadow-lg ${
                        isSelected 
                          ? 'ring-2 ring-rose-500 dark:ring-rose-400 bg-rose-50 dark:bg-rose-900/20' 
                          : 'dark:bg-slate-800'
                      }`}
                      onClick={() => togglePage(page.pageNum)}
                    >
                      <div className="relative">
                        <img
                          src={page.dataUrl}
                          alt={`Page ${page.pageNum}`}
                          className={`w-full h-auto transition-opacity ${isSelected ? 'opacity-50' : ''}`}
                        />
                        <div className="absolute top-2 left-2 flex items-center gap-2">
                          <Badge className={isSelected ? 'bg-rose-500' : 'bg-black/70'}>
                            {page.pageNum}
                          </Badge>
                        </div>
                        <div className="absolute top-2 right-2">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                            isSelected 
                              ? 'bg-rose-500 border-rose-500 text-white' 
                              : 'bg-white/80 border-slate-300'
                          }`}>
                            {isSelected && <Check className="w-4 h-4" />}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Trash2 className="w-12 h-12 text-rose-500" />
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}

            {selectedPages.size > 0 && (
              <div className="text-center">
                <Button 
                  onClick={removePagesAndSave}
                  disabled={isProcessing}
                  size="lg"
                  className="bg-gradient-to-r from-rose-500 to-pink-600"
                >
                  {isProcessing ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5 mr-2" />
                  )}
                  Remove {selectedPages.size} Page{selectedPages.size !== 1 ? 's' : ''} & Download
                </Button>
              </div>
            )}
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default RemovePdfPages;
